<template>
  <div class="container mx-auto px-4 py-4 text-gray-900 dark:text-gray-100">
    <!-- Registration Settings -->
    <div
      class="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 mb-6"
    >
      <div class="p-6 border-b border-gray-200 dark:border-gray-700">
        <h1 class="text-xl font-semibold text-gray-900 dark:text-white">Registration Settings</h1>
        <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Control user registration access
        </p>
      </div>
      <div class="p-6">
        <div class="flex items-center justify-between">
          <div>
            <h3 class="text-lg font-medium text-gray-900 dark:text-white">
              Disable registration page
            </h3>
            <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">
              When enabled, users will be redirected from the registration page back to the login
              page
            </p>
          </div>
          <div class="flex items-center">
            <span class="mr-3 text-sm text-gray-500 dark:text-gray-400">
              {{ registrationDisabled ? "Disabled" : "Enabled" }}
            </span>
            <button
              :disabled="settingsLoading"
              :class="[
                'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
                registrationDisabled ? 'bg-red-600' : 'bg-gray-200'
              ]"
              @click="toggleRegistration"
            >
              <span
                :class="[
                  'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
                  registrationDisabled ? 'translate-x-5' : 'translate-x-0'
                ]"
              />
            </button>
          </div>
        </div>
        <div v-if="settingsError" class="mt-4 text-sm text-red-600">
          {{ settingsError }}
        </div>
      </div>
    </div>

    <div class="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
      <div class="p-6 border-b border-gray-200 dark:border-gray-700">
        <div class="flex justify-between items-center">
          <div>
            <h2 class="text-xl font-semibold text-gray-900 dark:text-white">Users</h2>
            <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Total: {{ users.length }} users
            </p>
          </div>
          <div class="flex space-x-3">
            <Button
              label="Add New User"
              severity="primary"
              icon="pi pi-plus"
              @click="openInviteModal"
            />
          </div>
        </div>
      </div>

      <div class="p-6">
        <!-- Error Message -->
        <div
          v-if="error"
          class="mb-4 bg-red-50 dark:bg-red-900/40 border border-red-200 dark:border-red-700 rounded-md p-4"
        >
          <div class="flex">
            <div class="flex-shrink-0">
              <svg
                class="h-5 w-5 text-red-400 dark:text-red-300"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fill-rule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clip-rule="evenodd"
                />
              </svg>
            </div>
            <div class="ml-3">
              <h3 class="text-sm font-medium text-red-800 dark:text-red-300">Error</h3>
              <div class="mt-2 text-sm text-red-700 dark:text-red-200">{{ error }}</div>
            </div>
          </div>
        </div>

        <div v-if="loading" class="text-center py-8">
          <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
        </div>

        <div v-else-if="users.length === 0" class="text-center py-8">
          <p class="text-gray-500 dark:text-gray-400">No users found.</p>
        </div>

        <div v-else>
          <DataTable
            :value="users"
            :loading="loading"
            striped-rows
            paginator
            :rows="rows"
            :rows-per-page-options="[10, 25, 50, 100]"
            class="p-datatable-sm"
            :pt="{
              root: { class: 'border border-gray-200 dark:border-gray-700 rounded-lg' }
            }"
          >
            <Column field="username" header="User" sortable>
              <template #body="{ data }">
                <div class="flex items-center">
                  <div class="flex-shrink-0 h-10 w-10">
                    <div
                      class="h-10 w-10 rounded-full bg-gray-300 dark:bg-gray-700 flex items-center justify-center"
                    >
                      <span class="text-sm font-medium text-gray-700 dark:text-gray-200">
                        {{ data.username.charAt(0).toUpperCase() }}
                      </span>
                    </div>
                  </div>
                  <div class="ml-4">
                    <div class="text-sm font-medium text-gray-900 dark:text-white">
                      {{ data.username }}
                    </div>
                  </div>
                </div>
              </template>
            </Column>

            <Column field="email" header="Email" sortable>
              <template #body="{ data }">
                <div class="text-sm text-gray-900 dark:text-white">{{ data.email }}</div>
              </template>
            </Column>

            <Column field="role" header="Role" sortable>
              <template #body="{ data }">
                <span
                  :class="{
                    'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-200':
                      data.role === 'admin',
                    'bg-teal-100 text-teal-800 dark:bg-teal-900/40 dark:text-teal-200':
                      data.role === 'helper',
                    'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-200':
                      data.role === 'user'
                  }"
                  class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full"
                >
                  {{ data.role }}
                </span>
              </template>
            </Column>

            <Column field="isActive" header="Status" sortable>
              <template #body="{ data }">
                <span
                  :class="{
                    'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200':
                      data.isActive,
                    'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200': !data.isActive
                  }"
                  class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full"
                >
                  {{ data.isActive ? "Active" : "Inactive" }}
                </span>
              </template>
            </Column>

            <Column field="lastLogin" header="Last Login" sortable>
              <template #body="{ data }">
                <div class="text-sm text-gray-900 dark:text-white">
                  {{ data.lastLogin ? formatDate(data.lastLogin) : "Never" }}
                </div>
              </template>
            </Column>

            <Column header="Actions" :exportable="false" style="width: 150px">
              <template #body="{ data }">
                <div class="flex items-center gap-2">
                  <button
                    v-if="data.id === authStore.user?.id"
                    class="text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50 transition-colors"
                    title="Edit profile"
                    @click="openProfileModal"
                  >
                    <PencilIcon class="h-5 w-5" />
                  </button>
                  <template v-else>
                    <button
                      class="text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50 transition-colors"
                      title="Edit user"
                      @click="openEditUserModal(data)"
                    >
                      <PencilIcon class="h-5 w-5" />
                    </button>
                    <button
                      :disabled="deletingUserId === data.id"
                      class="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      :title="deletingUserId === data.id ? 'Deleting...' : 'Delete user'"
                      @click="handleDeleteUser(data)"
                    >
                      <TrashIcon class="h-5 w-5" />
                    </button>
                  </template>
                </div>
              </template>
            </Column>
          </DataTable>
        </div>
      </div>
    </div>

    <!-- Invite User Modal -->
    <div
      v-if="showInviteModal"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      @click.self="cancelInvite"
    >
      <div
        class="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 max-w-md w-full mx-4"
      >
        <div class="p-6">
          <div class="mb-6">
            <h2 class="text-2xl font-bold text-gray-900 dark:text-white">Invite New User</h2>
            <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Create a new user account and send invitation email
            </p>
          </div>

          <form class="space-y-4" @submit.prevent="handleInviteUser">
            <div>
              <label
                for="invite-username"
                class="block text-sm font-medium text-gray-700 dark:text-gray-200"
                >Username</label
              >
              <input
                id="invite-username"
                v-model="inviteForm.username"
                type="text"
                required
                class="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="Enter username"
              />
            </div>

            <div>
              <label
                for="invite-email"
                class="block text-sm font-medium text-gray-700 dark:text-gray-200"
                >Email</label
              >
              <input
                id="invite-email"
                v-model="inviteForm.email"
                type="email"
                required
                class="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="Enter email address"
              />
            </div>

            <div>
              <label
                for="invite-role"
                class="block text-sm font-medium text-gray-700 dark:text-gray-200"
                >Role</label
              >
              <select
                id="invite-role"
                v-model="inviteForm.role"
                class="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              >
                <option value="user">User</option>
                <option value="helper">Helper</option>
                <option value="admin">Admin</option>
              </select>
            </div>

            <div
              v-if="inviteError"
              class="text-red-600 dark:text-red-300 text-sm bg-red-50 dark:bg-red-900/40 border border-red-200 dark:border-red-700 rounded-md p-3"
            >
              {{ inviteError }}
            </div>

            <div class="flex justify-end space-x-3 pt-4 border-t border-gray-200">
              <Button type="button" label="Cancel" severity="secondary" @click="cancelInvite" />
              <Button
                type="submit"
                :disabled="inviteLoading"
                :label="inviteLoading ? 'Sending...' : 'Send Invitation'"
                severity="primary"
                :loading="inviteLoading"
              />
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- Profile Edit Modal -->
    <div
      v-if="showProfileModal"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      @click.self="cancelProfileEdit"
    >
      <div
        class="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto"
      >
        <div class="p-6">
          <div class="mb-6">
            <h2 class="text-2xl font-bold text-gray-900 dark:text-white">Edit Profile</h2>
            <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Update your profile information
            </p>
          </div>

          <form class="space-y-6" @submit.prevent="handleUpdateProfile">
            <div>
              <label
                for="profile-username"
                class="block text-sm font-medium text-gray-700 dark:text-gray-200"
                >Username</label
              >
              <input
                id="profile-username"
                v-model="profileForm.username"
                type="text"
                required
                class="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>

            <div>
              <label
                for="profile-email"
                class="block text-sm font-medium text-gray-700 dark:text-gray-200"
                >Email</label
              >
              <input
                id="profile-email"
                v-model="profileForm.email"
                type="email"
                required
                class="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>

            <div>
              <label
                for="profile-currentPassword"
                class="block text-sm font-medium text-gray-700 dark:text-gray-200"
                >Current Password</label
              >
              <input
                id="profile-currentPassword"
                v-model="profileForm.currentPassword"
                type="password"
                class="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="Leave blank to keep current password"
              />
            </div>

            <div>
              <label
                for="profile-newPassword"
                class="block text-sm font-medium text-gray-700 dark:text-gray-200"
                >New Password</label
              >
              <input
                id="profile-newPassword"
                v-model="profileForm.newPassword"
                type="password"
                class="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="Leave blank to keep current password"
              />
            </div>

            <div>
              <label
                for="profile-confirmPassword"
                class="block text-sm font-medium text-gray-700 dark:text-gray-200"
                >Confirm New Password</label
              >
              <input
                id="profile-confirmPassword"
                v-model="profileForm.confirmPassword"
                type="password"
                class="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="Leave blank to keep current password"
              />
            </div>

            <div
              v-if="profileError"
              class="text-red-600 dark:text-red-300 text-sm bg-red-50 dark:bg-red-900/40 border border-red-200 dark:border-red-700 rounded-md p-3"
            >
              {{ profileError }}
            </div>

            <div class="flex justify-end space-x-3 pt-4 border-t border-gray-200">
              <Button
                type="button"
                label="Cancel"
                severity="secondary"
                @click="cancelProfileEdit"
              />
              <Button
                type="submit"
                :disabled="profileLoading"
                :label="profileLoading ? 'Saving...' : 'Save'"
                severity="primary"
                :loading="profileLoading"
              />
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- Edit User Modal -->
    <div
      v-if="showEditUserModal"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      @click.self="cancelEditUser"
    >
      <div
        class="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto"
      >
        <div class="p-6">
          <div class="mb-6">
            <h2 class="text-2xl font-bold text-gray-900 dark:text-white">Edit User</h2>
            <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">Update user information</p>
          </div>

          <form class="space-y-6" @submit.prevent="handleUpdateUser">
            <div>
              <label
                for="edit-username"
                class="block text-sm font-medium text-gray-700 dark:text-gray-200"
                >Username</label
              >
              <input
                id="edit-username"
                v-model="editUserForm.username"
                type="text"
                required
                class="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>

            <div>
              <label
                for="edit-email"
                class="block text-sm font-medium text-gray-700 dark:text-gray-200"
                >Email</label
              >
              <input
                id="edit-email"
                v-model="editUserForm.email"
                type="email"
                required
                class="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>

            <div>
              <label
                for="edit-role"
                class="block text-sm font-medium text-gray-700 dark:text-gray-200"
                >Role</label
              >
              <select
                id="edit-role"
                v-model="editUserForm.role"
                class="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              >
                <option value="user">User</option>
                <option value="helper">Helper</option>
                <option value="admin">Admin</option>
              </select>
            </div>

            <div>
              <label
                for="edit-password"
                class="block text-sm font-medium text-gray-700 dark:text-gray-200"
                >New Password</label
              >
              <input
                id="edit-password"
                v-model="editUserForm.password"
                type="password"
                class="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="Leave blank to keep current password"
              />
              <p class="mt-1 text-xs text-gray-500">Leave blank to keep current password</p>
            </div>

            <div class="flex items-center">
              <input
                id="edit-isActive"
                v-model="editUserForm.isActive"
                type="checkbox"
                class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-600 rounded"
              />
              <label for="edit-isActive" class="ml-2 block text-sm text-gray-900 dark:text-white">
                Active
              </label>
              <p class="ml-4 text-xs text-gray-500 dark:text-gray-400">
                User account is active and can log in
              </p>
            </div>

            <div
              v-if="editUserError"
              class="text-red-600 dark:text-red-300 text-sm bg-red-50 dark:bg-red-900/40 border border-red-200 dark:border-red-700 rounded-md p-3"
            >
              {{ editUserError }}
            </div>

            <div class="flex justify-end space-x-3 pt-4 border-t border-gray-200">
              <Button type="button" label="Cancel" severity="secondary" @click="cancelEditUser" />
              <Button
                type="submit"
                :disabled="editUserLoading"
                :label="editUserLoading ? 'Saving...' : 'Save'"
                severity="primary"
                :loading="editUserLoading"
              />
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div
      v-if="showDeleteModal"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      @click.self="cancelDelete"
    >
      <div
        class="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 max-w-md w-full mx-4"
      >
        <div class="p-6">
          <div class="flex items-center mb-4">
            <div
              class="flex-shrink-0 mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 dark:bg-red-900/40"
            >
              <svg
                class="h-6 w-6 text-red-600 dark:text-red-300"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
          </div>
          <h3 class="text-lg font-medium text-gray-900 dark:text-white text-center mb-2">
            Delete User
          </h3>
          <p class="text-sm text-gray-500 dark:text-gray-400 text-center mb-6">
            Are you sure you want to delete <strong>{{ userToDelete?.username }}</strong
            >? This action cannot be undone.
          </p>
          <div class="flex space-x-3">
            <Button label="Cancel" severity="secondary" class="flex-1" @click="cancelDelete" />
            <Button
              :disabled="deletingUserId !== null"
              :label="deletingUserId !== null ? 'Deleting...' : 'Delete'"
              severity="danger"
              :loading="deletingUserId !== null"
              class="flex-1"
              @click="confirmDelete"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from "vue";
import { useToast } from "primevue/usetoast";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import Button from "primevue/button";
import { PencilIcon, TrashIcon } from "@heroicons/vue/24/outline";
import api from "../../services/api";
import { useAuthStore } from "../../stores/auth";

export default {
  name: "AdminUsers",
  components: {
    DataTable,
    Column,
    Button,
    PencilIcon,
    TrashIcon
  },
  setup() {
    const toast = useToast();
    const authStore = useAuthStore();
    const users = ref([]);
    const loading = ref(true);
    const error = ref("");
    const registrationDisabled = ref(false);
    const settingsLoading = ref(false);
    const settingsError = ref("");
    const showDeleteModal = ref(false);
    const userToDelete = ref(null);
    const deletingUserId = ref(null);
    const rows = ref(10);
    const showProfileModal = ref(false);
    const profileForm = ref({
      username: "",
      email: "",
      currentPassword: "",
      newPassword: "",
      confirmPassword: ""
    });
    const profileError = ref("");
    const profileLoading = ref(false);
    const showInviteModal = ref(false);
    const inviteForm = ref({
      username: "",
      email: "",
      role: "user"
    });
    const inviteError = ref("");
    const inviteLoading = ref(false);
    const showEditUserModal = ref(false);
    const editingUser = ref(null);
    const editUserForm = ref({
      username: "",
      email: "",
      role: "user",
      password: "",
      isActive: true
    });
    const editUserError = ref("");
    const editUserLoading = ref(false);

    const fetchUsers = async () => {
      try {
        loading.value = true;
        error.value = "";
        const response = await api.get("/api/users");
        if (response.data.success) {
          users.value = response.data.data.users;
        } else {
          error.value = response.data.message || "Failed to fetch users";
        }
      } catch (err) {
        console.error("Failed to fetch users:", err);
        error.value = err.response?.data?.message || "Failed to fetch users";
      } finally {
        loading.value = false;
      }
    };

    const formatDate = (dateString) => {
      return new Date(dateString).toLocaleDateString();
    };

    const fetchRegistrationSettings = async () => {
      try {
        settingsLoading.value = true;
        settingsError.value = "";
        const response = await api.get("/api/settings/registration");
        if (response.data.success) {
          registrationDisabled.value = !response.data.data.registrationEnabled;
        }
      } catch (err) {
        console.error("Failed to fetch registration settings:", err);
        settingsError.value =
          err.response?.data?.message || "Failed to fetch registration settings";
      } finally {
        settingsLoading.value = false;
      }
    };

    const toggleRegistration = async () => {
      try {
        settingsLoading.value = true;
        settingsError.value = "";
        const newStatus = !registrationDisabled.value;
        const response = await api.put("/api/settings/registration", {
          registrationEnabled: !newStatus
        });
        if (response.data.success) {
          registrationDisabled.value = newStatus;
        } else {
          settingsError.value = response.data.message || "Failed to update registration settings";
        }
      } catch (err) {
        console.error("Failed to update registration settings:", err);
        settingsError.value =
          err.response?.data?.message || "Failed to update registration settings";
      } finally {
        settingsLoading.value = false;
      }
    };

    const handleDeleteUser = (user) => {
      // FAILSAFE: Prevent self-deletion (frontend check)
      const currentUserId = authStore.user?.id;
      if (!currentUserId) {
        error.value = "Unable to verify user identity. Please refresh the page.";
        setTimeout(() => {
          error.value = "";
        }, 5000);
        return;
      }

      // Compare as both numbers and strings to handle type differences
      const userIdNum = typeof user.id === "number" ? user.id : parseInt(user.id);
      const currentUserIdNum =
        typeof currentUserId === "number" ? currentUserId : parseInt(currentUserId);

      if (userIdNum === currentUserIdNum || String(user.id) === String(currentUserId)) {
        error.value =
          "You cannot delete your own account. This action is blocked for security reasons.";
        setTimeout(() => {
          error.value = "";
        }, 5000);
        return;
      }

      userToDelete.value = user;
      showDeleteModal.value = true;
    };

    const cancelDelete = () => {
      showDeleteModal.value = false;
      userToDelete.value = null;
    };

    const openProfileModal = () => {
      if (authStore.user) {
        profileForm.value.username = authStore.user.username;
        profileForm.value.email = authStore.user.email;
        profileForm.value.currentPassword = "";
        profileForm.value.newPassword = "";
        profileForm.value.confirmPassword = "";
        profileError.value = "";
        showProfileModal.value = true;
      }
    };

    const cancelProfileEdit = () => {
      showProfileModal.value = false;
      profileForm.value = {
        username: "",
        email: "",
        currentPassword: "",
        newPassword: "",
        confirmPassword: ""
      };
      profileError.value = "";
    };

    const openEditUserModal = (user) => {
      editingUser.value = user;
      editUserForm.value = {
        username: user.username || "",
        email: user.email || "",
        role: user.role || "user",
        password: "",
        isActive: user.isActive !== undefined ? user.isActive : true
      };
      editUserError.value = "";
      showEditUserModal.value = true;
    };

    const cancelEditUser = () => {
      showEditUserModal.value = false;
      editingUser.value = null;
      editUserForm.value = {
        username: "",
        email: "",
        role: "user",
        password: "",
        isActive: true
      };
      editUserError.value = "";
    };

    const handleUpdateUser = async () => {
      try {
        editUserLoading.value = true;
        editUserError.value = "";

        if (!editingUser.value) {
          editUserError.value = "No user selected";
          return;
        }

        const updateData = {
          username: editUserForm.value.username,
          email: editUserForm.value.email,
          role: editUserForm.value.role,
          isActive: editUserForm.value.isActive
        };

        // Only include password if it's provided
        if (editUserForm.value.password && editUserForm.value.password.trim()) {
          updateData.password = editUserForm.value.password.trim();
        }

        const response = await api.put(`/api/users/${editingUser.value.id}`, updateData);

        if (response.data.success) {
          toast.add({
            severity: "success",
            summary: "Success",
            detail: "User updated successfully",
            life: 3000
          });

          // Refresh users list
          await fetchUsers();
          cancelEditUser();
        } else {
          editUserError.value = response.data.message || "Failed to update user";
        }
      } catch (err) {
        console.error("Update user error:", err);
        editUserError.value =
          err.response?.data?.message ||
          err.response?.data?.errors?.join(", ") ||
          "Failed to update user";
      } finally {
        editUserLoading.value = false;
      }
    };

    const openInviteModal = () => {
      inviteForm.value = {
        username: "",
        email: "",
        role: "user"
      };
      inviteError.value = "";
      showInviteModal.value = true;
    };

    const cancelInvite = () => {
      showInviteModal.value = false;
      inviteForm.value = {
        username: "",
        email: "",
        role: "user"
      };
      inviteError.value = "";
    };

    const handleInviteUser = async () => {
      try {
        inviteLoading.value = true;
        inviteError.value = "";

        const response = await api.post("/api/users/invite", {
          username: inviteForm.value.username,
          email: inviteForm.value.email,
          role: inviteForm.value.role
        });

        if (response.data.success) {
          toast.add({
            severity: "success",
            summary: "Success",
            detail: "User invited successfully! Invitation email has been sent.",
            life: 3000
          });
          // Refresh users list
          await fetchUsers();
          cancelInvite();
        } else {
          inviteError.value = response.data.message || "Failed to invite user";
        }
      } catch (err) {
        console.error("Invite user error:", err);
        inviteError.value =
          err.response?.data?.message ||
          err.response?.data?.errors?.join(", ") ||
          "Failed to invite user";
      } finally {
        inviteLoading.value = false;
      }
    };

    const handleUpdateProfile = async () => {
      if (
        profileForm.value.newPassword &&
        profileForm.value.newPassword !== profileForm.value.confirmPassword
      ) {
        profileError.value = "New passwords do not match";
        return;
      }

      try {
        profileLoading.value = true;
        profileError.value = "";

        const updateData = {
          username: profileForm.value.username,
          email: profileForm.value.email
        };

        if (profileForm.value.newPassword) {
          updateData.currentPassword = profileForm.value.currentPassword;
          updateData.newPassword = profileForm.value.newPassword;
        }

        const result = await authStore.updateProfile(updateData);

        if (result.success) {
          toast.add({
            severity: "success",
            summary: "Success",
            detail: "Profile updated successfully",
            life: 3000
          });
          // Update the user in the users list
          const currentUserIndex = users.value.findIndex((u) => u.id === authStore.user?.id);
          if (currentUserIndex !== -1 && authStore.user) {
            users.value[currentUserIndex] = { ...users.value[currentUserIndex], ...authStore.user };
          }
          // Clear password fields
          profileForm.value.currentPassword = "";
          profileForm.value.newPassword = "";
          profileForm.value.confirmPassword = "";
          cancelProfileEdit();
        } else {
          profileError.value = result.message || "Failed to update profile";
        }
      } catch (err) {
        profileError.value = "An unexpected error occurred";
      } finally {
        profileLoading.value = false;
      }
    };

    const confirmDelete = async () => {
      if (!userToDelete.value) return;

      // FAILSAFE: Final check before deletion (in case state changed)
      const currentUserId = authStore.user?.id;
      const userIdToDelete = userToDelete.value.id;

      if (
        currentUserId &&
        (userIdToDelete === currentUserId || String(userIdToDelete) === String(currentUserId))
      ) {
        error.value =
          "You cannot delete your own account. This action is blocked for security reasons.";
        showDeleteModal.value = false;
        userToDelete.value = null;
        setTimeout(() => {
          error.value = "";
        }, 5000);
        return;
      }

      try {
        deletingUserId.value = userToDelete.value.id;
        error.value = "";

        const response = await api.delete(`/api/users/${userToDelete.value.id}`);

        if (response.data.success) {
          // Remove user from local list
          users.value = users.value.filter((u) => u.id !== userToDelete.value.id);
          showDeleteModal.value = false;
          userToDelete.value = null;
        } else {
          error.value = response.data.message || "Failed to delete user";
        }
      } catch (err) {
        console.error("Failed to delete user:", err);
        // Check if error is about self-deletion
        const errorMessage = err.response?.data?.message || "Failed to delete user";
        if (
          errorMessage.includes("cannot delete your own account") ||
          errorMessage.includes("your own account")
        ) {
          error.value =
            "You cannot delete your own account. This action is blocked for security reasons.";
        } else {
          error.value = errorMessage;
        }
      } finally {
        deletingUserId.value = null;
      }
    };

    onMounted(() => {
      fetchUsers();
      fetchRegistrationSettings();
    });

    return {
      authStore,
      users,
      loading,
      error,
      fetchUsers,
      formatDate,
      registrationDisabled,
      settingsLoading,
      settingsError,
      toggleRegistration,
      showDeleteModal,
      userToDelete,
      deletingUserId,
      handleDeleteUser,
      cancelDelete,
      confirmDelete,
      openProfileModal,
      cancelProfileEdit,
      handleUpdateProfile,
      showProfileModal,
      profileForm,
      profileError,
      profileLoading,
      openInviteModal,
      cancelInvite,
      handleInviteUser,
      showInviteModal,
      inviteForm,
      inviteError,
      inviteLoading,
      rows,
      openEditUserModal,
      cancelEditUser,
      handleUpdateUser,
      showEditUserModal,
      editUserForm,
      editUserError,
      editUserLoading
    };
  }
};
</script>

<style>
.dark .p-datatable table {
  background-color: #111827;
  color: #e5e7eb;
}

.dark .p-datatable .p-datatable-thead > tr > th {
  background-color: #1f2937;
  color: #e5e7eb;
  border-color: #374151;
}

.dark .p-datatable .p-datatable-tbody > tr > td {
  background-color: #111827;
  color: #e5e7eb;
  border-color: #1f2937;
}

.dark .p-datatable .p-datatable-tbody > tr.p-highlight > td {
  background-color: #1f2937;
}

.dark .p-datatable .p-datatable-paginator-bottom {
  background-color: #0f172a;
  color: #e5e7eb;
  border-top: 1px solid #1f2937;
}

.dark .p-paginator .p-paginator-page,
.dark .p-paginator .p-paginator-prev,
.dark .p-paginator .p-paginator-next,
.dark .p-paginator .p-paginator-first,
.dark .p-paginator .p-paginator-last {
  background-color: #0f172a;
  color: #e5e7eb;
  border-color: #1f2937;
}

.dark .p-paginator .p-paginator-page.p-highlight {
  background-color: #1f2937;
  color: #d1d5db;
  border-color: #334155;
}
</style>
